import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useUserStore from "@store/useUserStore";
import { getMySchedule, getMyTickets, getPracticeTickets } from "api/user";
import { getMyTours } from "api/travel";
import dayjs from "dayjs";
import { getKstDday } from "utils/util";
import bucketIcon from "assets/images/icons/buccl_logo_B.png";

const ActivityTab = ({ onTabChange, variant = "activity" }) => {
    const navigate = useNavigate();
    const userInfo = useUserStore((state) => state.userInfo);
    const [nextDday, setNextDday] = useState("-");
    const [ticketCount, setTicketCount] = useState(0);

    useEffect(() => {
        if (userInfo !== null) {
            Promise.all([
                getMyTickets().then((r) => r ?? []).catch(() => []),
                getPracticeTickets().then((r) => r ?? []).catch(() => []),
            ]).then(([regular, practice]) => {
                const visibleRegular = (regular || []).filter(
                    (t) => t.status !== "CANCELLED"
                );
                setTicketCount((visibleRegular.length || 0) + (practice?.length || 0));
            });

            Promise.all([
                getMySchedule().catch(() => ({ lessons: [], practices: [] })),
                getMyTours(false).catch(() => []),
            ]).then(([scheduleRes, tours]) => {
                const today = dayjs().subtract(1, "day");

                // 강습/실습 날짜
                const lessonDates = [...scheduleRes.lessons, ...scheduleRes.practices]
                    .map((item) => item?.schedule_info?.date || item?.date)
                    .filter((date) => dayjs(date).isAfter(today));

                // 투어 날짜
                const tourDates = tours
                    .filter((t) => t.status !== "COMPLETED")
                    .map((t) => t.start_date)
                    .filter((date) => dayjs(date).isAfter(today));

                const allDates = [...lessonDates, ...tourDates].sort(
                    (a, b) => new Date(a) - new Date(b)
                );

                if (allDates.length > 0) {
                    setNextDday(getKstDday(allDates[0]) ?? "-");
                }
            });
        }
    }, [userInfo]);

    // 강사관리 탭(강사 전용) 메뉴
    const instructorMenuItems = [
        {
            icon: "🗓️",
            label: "스케쥴 관리",
            badge: null,
            onClick: () => navigate("/attendance?view=calendar"),
        },    
        {
            icon: "🛎️",
            label: "강습 수락 내역",
            badge: null,
            onClick: () => navigate("/lesson-requests/instructor"),
        }, 
        {
            icon: "📊",
            label: "월별 통계",
            badge: null,
            onClick: () => navigate("/instructor/stats"),
        },
        {
            icon: "🧭",
            label: "내가 개설한 투어",
            badge: null,
            onClick: () => navigate("/tour/my"),
        },
        {
            icon: "➕",
            label: "투어 개설",
            badge: null,
            onClick: () => navigate("/tour/regist"),
        },
    ];

    // 나의 활동 탭 공통 메뉴
    const activityMenuItems = [
      {
        icon: "🪪",
        label: "자격증",
        badge: null,
        onClick: () => navigate("/verify/student"),
    },
        {
            icon: "📝",
            label: "수업개설 요청",
            badge: null,
            onClick: () => navigate("/lesson-requests"),
        },
        {
            iconSrc: bucketIcon,
            label: "나의 버킷리스트",
            badge: null,
            onClick: () => navigate("/mybuccl/bucketlist"),
        },
        {
            icon: "🎫",
            label: "보유 수강권",
            badge: `${ticketCount}장`,
            onClick: () => navigate("/myticket"),
        },
        // {
        //     icon: "📋",
        //     label: "수강 내역",
        //     badge: null,
        //     onClick: () => navigate("/mybuccl/lessons"),
        // },
        {
            icon: "✈️",
            label: "투어 내역",
            badge: null,
            onClick: () => navigate("/mybuccl/tours"),
        },
        {
            icon: "🤍",
            label: "찜한 투어",
            badge: null,
            onClick: () => navigate("/mybuccl/tour-likes"),
        },
        {
            icon: "🛒",
            label: "주문 내역",
            badge: null,
            onClick: () => navigate("/order-history"),
        },
        {
            icon: "📍",
            label: "주소 관리",
            badge: null,
            onClick: () => navigate("/mypage/address/edit"),
        },
    ];

    // 커뮤니티 탭 메뉴
    const communityMenuItems = [
        {
            icon: "✏️",
            label: "내가 쓴 글",
            badge: null,
            onClick: () => {
                if (onTabChange) {
                    onTabChange("피드");
                } else {
                    navigate("/community");
                }
            },
        },
        {
            icon: "❤️",
            label: "좋아요 한 글",
            badge: null,
            onClick: () => window.open("/mybuccl/liked", "_blank"),
        },
        {
            icon: "🚫",
            label: "차단 사용자 관리",
            badge: null,
            onClick: () => window.open("/community/blocked", "_blank"),
        },
        {
            icon: "🚨",
            label: "내 신고 내역",
            badge: null,
            onClick: () => window.open("/community/reports", "_blank"),
        },
    ];

    const menuItemsByVariant = {
        instructor: instructorMenuItems,
        community: communityMenuItems,
        activity: activityMenuItems,
    };

    const menuItems = menuItemsByVariant[variant] ?? activityMenuItems;

    return (
        <div className="activity-tab">
            <ul className="activity-menu-list">
                {menuItems.map((item, idx) => (
                    <li
                        key={`activity-${idx}`}
                        className="activity-menu-item"
                        onClick={item.onClick}
                    >
                        {item.iconSrc ? (
                            <img
                                className="activity-menu-icon activity-menu-icon--img"
                                src={item.iconSrc}
                                alt=""
                            />
                        ) : (
                            <span className="activity-menu-icon">{item.icon}</span>
                        )}
                        <span className="activity-menu-label">{item.label}</span>
                        <div className="activity-menu-right">
                            {item.badge !== null && (
                                <span className="activity-menu-badge">
                                    {item.badge}
                                </span>
                            )}
                            <span className="activity-menu-arrow">›</span>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default ActivityTab;
